import { computed } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

interface UseSelectAllOptions<TValue = string | number> {
  modelValue: Ref<TValue[] | undefined>
  selectedValues: ComputedRef<TValue[]>
  displayedOptions: ComputedRef<AppSelectOption<TValue>[]>
}

interface UseSelectAllReturn {
  /** True when every enabled visible option is in the model (and there is at
   *  least one enabled visible option). */
  isAllSelected: ComputedRef<boolean>
  /** True when there are no enabled visible options to toggle. */
  isSelectAllDisabled: ComputedRef<boolean>
  /** Toggle all enabled visible options in the model. Selections outside the
   *  visible set are preserved. */
  toggleSelectAll: () => void
}

/**
 * "Select all" behavior for multi-selects, scoped to the currently-visible
 * enabled options. Shared by `useAppMultiSelect` and
 * `useAppMultiSelectSearchable` (where `displayedOptions` reflects the
 * filtered / paginated set).
 */
export const useSelectAll = <TValue = string | number>(
  options: UseSelectAllOptions<TValue>,
): UseSelectAllReturn => {
  const { modelValue, selectedValues, displayedOptions } = options

  const selectAllEnabledOptions = computed<AppSelectOption<TValue>[]>(
    () => displayedOptions.value.filter(o => !o.disabled),
  )

  const isAllSelected = computed<boolean>(() => {
    const enabled = selectAllEnabledOptions.value
    if (enabled.length === 0) return false
    return enabled.every(o => selectedValues.value.includes(o.value))
  })

  const isSelectAllDisabled = computed<boolean>(
    () => selectAllEnabledOptions.value.length === 0,
  )

  const toggleSelectAll = (): void => {
    if (isSelectAllDisabled.value) return
    const enabled = selectAllEnabledOptions.value
    if (isAllSelected.value) {
      const enabledValues = new Set(enabled.map(o => o.value))
      modelValue.value = selectedValues.value.filter(v => !enabledValues.has(v))
      return
    }
    const current = selectedValues.value
    const missing = enabled
      .map(o => o.value)
      .filter(v => !current.includes(v))
    modelValue.value = [...current, ...missing]
  }

  return {
    isAllSelected,
    isSelectAllDisabled,
    toggleSelectAll,
  }
}
